<template>
  <div data-qa="core_file_picker">
    <CaptureTypeList
      v-model="isOpenedCaptureTypeList"
      @choice-capture="handleChoiceCapture"
    />
    <label
      class="outline-none"
      :disabled="disabled"
      :tabindex="disabled ? -1 : 0"
      @click="open"
      @blur="handleBlur"
      @focus="handleFocus"
      @keydown="handleKeydown"
    >
      <slot
        :focused="isFocused"
        :blured="isBlured"
        :disabled="disabled"
      />
      <input
        ref="coreFilePickerRef"
        data-qa="core_file_picker_input"
        class="file-input"
        type="file"
        :accept="accept"
        :capture="capture"
        disabled
        @change="handleSelectedFile"
      >
    </label>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import heic2any from 'heic2any'
import { throttle } from 'lodash'
import { FileExtensions, MimeTypes, MIME_TYPES } from 'www/constants/fileTypes'
import type {
  CaptureType,
  FileValidationRule,
  ImageValidationRule,
} from 'src/core/CoreFilePicker/types'
import readFileAsDataUrl from 'src/core/CoreFilePicker/functions/readFileAsDataUrl'
import useValidate from 'src/core/CoreFilePicker/composables/useValidate'
import useHeic from 'src/core/CoreFilePicker/composables/useHeic'
import useUiEvent from 'src/core/CoreFilePicker/composables/useUiEvent'
import CaptureTypeList from 'src/core/CoreFilePicker/components/CaptureTypeList.vue'

const props: {
  acceptFileTypes?: Array<MimeTypes | FileExtensions>
  fileValidationRules?: FileValidationRule[]
  imageValidationRules?: ImageValidationRule[]
  isConvertToBase64?: boolean
  disabled?: boolean
} = defineProps<{
  acceptFileTypes?: Array<
    '.pdf'
    | '.doc'
    | '.docx'
    | '.xls'
    | '.xlsx'
    | '.png'
    | '.jpeg'
    | '.jpg'
    | '.webp'
    | '.svg'
    | '.heic'
    | '.heif'
    | '*'
    | 'application/pdf'
    | 'application/msword'
    | 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    | 'application/vnd.ms-excel'
    | 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    | 'image/png'
    | 'image/jpeg'
    | 'image/webp'
    | 'image/svg+xml'
    | 'image/heic'
    | 'image/heif'
  >
  fileValidationRules?: Array<{
    validate:(_file: File) => boolean
    error: any
  }>
  imageValidationRules?: Array<{
    validate:(_img: HTMLImageElement) => boolean
    error: any
  }>
  isConvertToBase64?: boolean
  disabled?: boolean
}>()

type Emits = {
  (_event: 'validation-error', _value: any): void
  (_event: 'pick-file-error'): void
  (_event: 'input', _value: { file: File, base64File?: string }): void
  (_event: 'jest', _value: string): void
}

const emit = defineEmits<Emits>()

const coreFilePickerRef = ref()
const accept = computed(() => (props.acceptFileTypes?.length
  ? `${props.acceptFileTypes.join(', ')},.heic`
  : MIME_TYPES.all))

const isOpenedCaptureTypeList = ref(false)
const capture = ref<CaptureType>(false)

const { validateFile, validateImage } = useValidate()
const { checkHeicNoSafari } = useHeic()

const isMobile = ref(false)

async function validate(file: File, isHeicDesktop: boolean) {
  const fileValidationResult = isHeicDesktop || await validateFile(file, props.fileValidationRules)

  if (fileValidationResult !== true) {
    return fileValidationResult
  }

  return validateImage(file, props.imageValidationRules)
}

async function handleSelectedFile({ target }: Event) {
  emit('jest', 'handleSelectedFile')

  if (!target || !(target instanceof HTMLInputElement)) {
    return
  }

  const file = target!.files?.[0] as File

  coreFilePickerRef.value.value = ''

  const isHeicDesktop = !isMobile.value && checkHeicNoSafari(file)

  try {
    const validationResult = await validate(file, isHeicDesktop)

    if (validationResult !== true) {
      emit('validation-error', validationResult)

      return
    }

    if (props.isConvertToBase64) {
      const convertedFile: File | Blob = isHeicDesktop
        ? await heic2any({
          blob: file,
          toType: 'image/jpeg',
        }) as Blob
        : file

      const base64File = await readFileAsDataUrl(convertedFile)

      emit('input', { file, base64File })
    } else {
      emit('input', { file })
    }
  } catch (_error) {
    emit('pick-file-error')
  }
}

function openFilePicker() {
  emit('jest', 'openFilePicker')
  coreFilePickerRef.value.disabled = false
  coreFilePickerRef.value.click()
  coreFilePickerRef.value.disabled = true
}

function handleChoiceCapture(value: CaptureType) {
  emit('jest', 'handleChoiceCapture')
  capture.value = value
  openFilePicker()
  isOpenedCaptureTypeList.value = false
}

const open = throttle(() => {
  emit('jest', 'open')

  if (props.disabled) {
    return
  }

  if (isMobile.value) {
    isOpenedCaptureTypeList.value = true
  } else {
    capture.value = false
    openFilePicker()
  }
}, 300, { leading: true, trailing: false })

const {
  isBlured,
  isFocused,
  handleBlur,
  handleFocus,
  handleKeydown,
} = useUiEvent(open)

onMounted(() => {
  isMobile.value = window.MOBILE_VERSION
})

defineExpose({
  open,
  jest: {
    accept,
    capture,
    coreFilePickerRef,
    emit,
    props,
    isBlured,
    isFocused,
    isOpenedCaptureTypeList,
    handleChoiceCapture,
    handleSelectedFile,
    open,
    openFilePicker,
  },
})
</script>

<style lang="scss" scoped>
.file-input {
  inset: 0;
  opacity: 0;
  position: absolute;
  pointer-events: none;
}

.outline-none {
  outline: none;
}
</style>
